import { PreviewSectionContext } from '../../../utils/previewMapSections';
import { TeachingBlockCardType } from '../types';

/** Eine Zeile der Vorschau-Karte – bewusst minimal, damit sie für alle drei Kontexte taugt. */
export type PreviewRow = { uuid: string; name: string };

/** Strukturelle Minimalform einer Karte: nur die Felder, aus denen Zeilen abgeleitet werden. */
export type PreviewCardLike = Pick<TeachingBlockCardType, 'classes' | 'teachers' | 'rooms' | 'lesson' | 'subject'>;

type ClassLike = { uuid: string; name?: string | null; shortName?: string | null };
type TeacherLike = { uuid: string; shortName?: string | null; displayNameShort?: string | null };
type RoomLike = { uuid: string; name?: string | null };

const byName = (a: PreviewRow, b: PreviewRow) => a.name.localeCompare(b.name, undefined, { numeric: true });

/**
 * Zeilenliste eines Abschnitts. Die Namensauswahl entspricht dem Hauptboard (Kurzname bevorzugt),
 * damit man Vorschau und Board ohne Umdenken nebeneinander lesen kann.
 */
export function buildPreviewRows(
  context: PreviewSectionContext,
  source: { classes: ClassLike[]; teachers: TeacherLike[]; rooms: RoomLike[] },
): PreviewRow[] {
  if (context === 'teachers') {
    return source.teachers
      .map((teacher) => ({ uuid: teacher.uuid, name: teacher.displayNameShort ?? teacher.shortName ?? '' }))
      .sort(byName);
  }
  if (context === 'rooms') {
    return source.rooms.map((room) => ({ uuid: room.uuid, name: room.name ?? '' })).sort(byName);
  }
  return source.classes
    .map((schoolClass) => ({ uuid: schoolClass.uuid, name: schoolClass.shortName ?? schoolClass.name ?? '' }))
    .sort(byName);
}

/**
 * Raumvorrat einer Karte = derselbe Pool wie in „Karte bearbeiten": Lesson-/Fach-/Lehrer-/
 * Klassenräume plus die bereits zugewiesenen Räume. Die Gruppe „school" (alle Räume) bleibt
 * bewusst draußen – sonst wäre die Einschränkung wirkungslos.
 */
export function cardRoomUuids(card: PreviewCardLike): Set<string> {
  const uuids = new Set<string>();
  card.rooms.forEach((room) => uuids.add(room.uuid));
  card.lesson?.roomSupplyConnection.edges.forEach((edge) => uuids.add(edge.node.uuid));
  card.subject?.defaultRooms.forEach((room) => uuids.add(room.uuid));
  card.teachers.forEach((teacher) => teacher.defaultRoom && uuids.add(teacher.defaultRoom.uuid));
  card.classes.forEach((schoolClass) => schoolClass.defaultRoom && uuids.add(schoolClass.defaultRoom.uuid));
  return uuids;
}

export function cardTeacherUuids(card: PreviewCardLike): Set<string> {
  return new Set(card.teachers.map((teacher) => teacher.uuid));
}

export function cardClassUuids(card: PreviewCardLike): Set<string> {
  return new Set(card.classes.map((schoolClass) => schoolClass.uuid));
}

/**
 * Alle Klassen, in denen mindestens eine der übergebenen Lehrkräfte irgendein Kärtchen hat.
 * Im Lehrer-Board ist genau das die interessante Frage: Wo ist diese Lehrkraft sonst noch
 * unterwegs? – nicht nur die Klassen der Karte in der Hand.
 */
export function teacherClassUuids(cards: PreviewCardLike[], teacherUuids: Set<string>): Set<string> {
  const uuids = new Set<string>();
  if (teacherUuids.size === 0) return uuids;
  cards.forEach((card) => {
    if (!card.teachers.some((teacher) => teacherUuids.has(teacher.uuid))) return;
    card.classes.forEach((schoolClass) => uuids.add(schoolClass.uuid));
  });
  return uuids;
}

export type AllowedRowParams = {
  section: PreviewSectionContext;
  boardContext: string;
  /** Automatik-Ansicht (mehrere Abschnitte) – nur dort wird der Klassen-Abschnitt eingeschränkt. */
  auto: boolean;
  card: PreviewCardLike | undefined;
  /** Alle gesteckten Karten – nur für die Lehrer-Board-Sicht auf den Klassen-Abschnitt nötig. */
  cards: PreviewCardLike[];
};

/**
 * Zeilen-Einschränkung eines Abschnitts, `null` = keine Einschränkung (alle Zeilen).
 * Ohne Karte in der Hand gibt es keinen Bezugspunkt → immer alles zeigen.
 */
export function allowedRowUuids({ section, boardContext, auto, card, cards }: AllowedRowParams): Set<string> | null {
  if (!card) return null;
  if (section === 'teachers') return cardTeacherUuids(card);
  if (section === 'rooms') return cardRoomUuids(card);
  // Klassen: in der Ein-Kontext-Ansicht bewusst ungefiltert (Orientierung über den ganzen Plan).
  if (!auto) return null;
  if (boardContext !== 'teachers') return cardClassUuids(card);
  // Im Lehrer-Board zählt die Karte in der Hand selbst mit – sie kann (aus dem Stapel gezogen)
  // bereits aus den gesteckten Karten verschwunden sein.
  const uuids = teacherClassUuids(cards, cardTeacherUuids(card));
  cardClassUuids(card).forEach((uuid) => uuids.add(uuid));
  return uuids;
}

export function filterPreviewRows(rows: PreviewRow[], allowed: Set<string> | null): PreviewRow[] {
  if (!allowed) return rows;
  return rows.filter((row) => allowed.has(row.uuid));
}

/** Zeilen der Karte in der Hand im jeweiligen Abschnitt → Hervorhebung (Zeilenname + Zellenanker). */
export function pickedResourceUuids(section: PreviewSectionContext, card: PreviewCardLike | undefined): Set<string> {
  if (!card) return new Set<string>();
  if (section === 'teachers') return cardTeacherUuids(card);
  if (section === 'rooms') return new Set(card.rooms.map((room) => room.uuid));
  return cardClassUuids(card);
}
